(function () {
  if (customElements.get('form-builder-widget')) return;

  var ITI_VERSION = '29.5.2';
  var ITI_BASE = 'https://cdn.jsdelivr.net/npm/intl-tel-input@' + ITI_VERSION + '/dist';
  var itiLoadPromise = null;

  function loadIntlTelInput() {
    if (window.intlTelInput) return Promise.resolve();
    if (itiLoadPromise) return itiLoadPromise;

    itiLoadPromise = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-form-builder-iti-css]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = ITI_BASE + '/css/intlTelInput.min.css';
        link.setAttribute('data-form-builder-iti-css', 'true');
        document.head.appendChild(link);
      }
      var script = document.createElement('script');
      script.src = ITI_BASE + '/js/intlTelInput.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return itiLoadPromise;
  }

  class FormBuilderWidget extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.form-builder-form');
      this.mode = this.dataset.mode;
      this.delivery = this.dataset.delivery;

      if (this.form) this.initForm();
      this.initDropzones();
      this.initPhoneFields();
      if (this.mode === 'popup') {
        this.initPopup();
        if (this.dataset.autoOpen === 'true') this.openPopup();
      }
    }

    initForm() {
      const honey = this.form.querySelector('.form-builder-honey');

      if (this.delivery === 'custom') {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          if (honey && honey.value) return; // silently drop bot submissions
          this.submitViaAjax();
        });
      }
      // delivery === 'native' posts normally (full page reload), no JS needed.
    }

    initDropzones() {
      this.querySelectorAll('[data-form-builder-dropzone]').forEach((zone) => {
        const input = zone.querySelector('.form-builder-dropzone-input');
        const list = zone.querySelector('.form-builder-dropzone-files');
        const errorEl = zone.querySelector('.form-builder-dropzone-error');
        const maxSize = parseInt(zone.dataset.maxSize || '0', 10);
        if (!input || !list) return;

        const showError = (message) => {
          if (!errorEl) return;
          errorEl.textContent = message;
          errorEl.hidden = !message;
          zone.classList.toggle('has-error', Boolean(message));
        };

        const enforceMaxSize = () => {
          if (!maxSize) return;
          const all = Array.from(input.files || []);
          const tooBig = all.filter((f) => f.size > maxSize);
          if (tooBig.length === 0) {
            showError('');
            return;
          }
          const kept = all.filter((f) => f.size <= maxSize);
          const transfer = new DataTransfer();
          kept.forEach((f) => transfer.items.add(f));
          input.files = transfer.files;
          const limitLabel = this.formatFileSize(maxSize);
          const names = tooBig.map((f) => f.name).join(', ');
          showError(`${names} exceeded the ${limitLabel} limit and ${tooBig.length > 1 ? 'were' : 'was'} not added.`);
        };

        const render = () => {
          const files = Array.from(input.files || []);
          list.innerHTML = '';
          list.hidden = files.length === 0;

          files.forEach((file, index) => {
            const row = document.createElement('li');
            row.className = 'form-builder-dropzone-file';
            row.innerHTML =
              '<span class="form-builder-dropzone-file-icon" aria-hidden="true">' +
              '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' +
              '</span>' +
              '<span class="form-builder-dropzone-file-meta">' +
              '<span class="form-builder-dropzone-file-name"></span>' +
              '<span class="form-builder-dropzone-file-size"></span>' +
              '</span>' +
              '<button type="button" class="form-builder-dropzone-file-remove" aria-label="Remove file">' +
              '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
              '</button>';
            row.querySelector('.form-builder-dropzone-file-name').textContent = file.name;
            row.querySelector('.form-builder-dropzone-file-size').textContent = this.formatFileSize(file.size);
            row.querySelector('.form-builder-dropzone-file-remove').addEventListener('click', () => {
              this.removeFileAt(input, index);
              render();
            });
            list.appendChild(row);
          });
        };

        input.addEventListener('change', () => {
          enforceMaxSize();
          render();
        });
        zone.addEventListener('dragenter', () => zone.classList.add('is-dragover'));
        zone.addEventListener('dragleave', () => zone.classList.remove('is-dragover'));
        zone.addEventListener('drop', () => zone.classList.remove('is-dragover'));

        render();
      });
    }

    initPhoneFields() {
      const inputs = Array.from(this.querySelectorAll('.js-form-builder-phone'));
      if (inputs.length === 0) return;

      loadIntlTelInput()
        .then(() => {
          inputs.forEach((input) => {
            const fieldName = input.dataset.fieldName;
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = fieldName;
            input.insertAdjacentElement('afterend', hidden);

            const iti = window.intlTelInput(input, {
              initialCountryLookup: async () => {
                try {
                  const res = await fetch('https://ipapi.co/json');
                  const data = await res.json();
                  return data.country_code || 'us';
                } catch (e) {
                  return 'us';
                }
              },
              loadUtils: () => import(ITI_BASE + '/js/utils.js'),
            });

            // Sync the hidden field on every change rather than relying on the
            // plugin's own submit-time hook — this form widget attaches its own
            // 'submit' listener too, and listener order between the two isn't
            // guaranteed, so the hidden value must already be correct beforehand.
            const sync = () => {
              hidden.value = input.value ? iti.getNumber() : '';
            };
            input.addEventListener('input', sync);
            input.addEventListener('blur', sync);
            input.addEventListener('countrychange', sync);
          });
        })
        .catch(() => {
          // Plugin failed to load — the plain tel input still submits under
          // its data-field-name via the fallback name attribute below.
          inputs.forEach((input) => {
            if (!input.name) input.name = input.dataset.fieldName;
          });
        });
    }

    removeFileAt(input, index) {
      const remaining = Array.from(input.files || []).filter((_, i) => i !== index);
      const transfer = new DataTransfer();
      remaining.forEach((file) => transfer.items.add(file));
      input.files = transfer.files;
    }

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    setLoading(isLoading) {
      const btn = this.form.querySelector('.form-builder-submit');
      if (!btn) return;
      btn.disabled = isLoading;
      btn.dataset.loading = isLoading ? 'true' : 'false';
    }

    showMessage(type, text) {
      const successEl = this.form.querySelector('.form-builder-message--success');
      const errorEl = this.form.querySelector('.form-builder-message--error');
      [successEl, errorEl].forEach((el) => el && el.classList.remove('is-visible'));
      const target = type === 'success' ? successEl : errorEl;
      if (target) {
        if (text) target.textContent = text;
        target.classList.add('is-visible');
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    arrayBufferToBase64(buffer) {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
      }
      return btoa(binary);
    }

    async relayFilesToDrive(formData) {
      const driveUrl = this.dataset.driveUrl;
      if (!driveUrl) return;
      const fileEntries = Array.from(formData.entries()).filter(([, v]) => v instanceof File && v.size > 0);
      if (fileEntries.length === 0) return;

      // Apps Script's doPost() does not reliably parse real multipart file blobs,
      // so files are base64-encoded and sent as plain text fields instead — the
      // Code.gs on the other end decodes them with Utilities.base64Decode().
      const driveData = new FormData();
      for (const [key, value] of formData.entries()) {
        if (!(value instanceof File)) driveData.append(key, value);
      }
      for (const [key, file] of fileEntries) {
        const buffer = await file.arrayBuffer();
        driveData.append(key + '__b64', this.arrayBufferToBase64(buffer));
        driveData.append(key + '__name', file.name);
        driveData.append(key + '__type', file.type || 'application/octet-stream');
      }

      // Fire-and-forget: Apps Script Web Apps typically don't return CORS headers,
      // so the response can't be read from here — this only best-effort ships a copy.
      fetch(driveUrl, { method: 'POST', mode: 'no-cors', body: driveData }).catch(() => {});
    }

    submitViaAjax() {
      const action = this.form.getAttribute('action');
      const formData = new FormData(this.form);
      this.relayFilesToDrive(formData).catch(() => {});

      this.setLoading(true);

      fetch(action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) throw new Error('Request failed');
          return response.json().catch(() => ({}));
        })
        .then(() => {
          this.setLoading(false);
          this.showMessage('success');
          this.form.reset();
          this.markSubmitted();

          const redirect = this.dataset.redirect;
          if (redirect) {
            setTimeout(() => {
              window.location.href = redirect;
            }, 1200);
          } else if (this.mode === 'popup') {
            setTimeout(() => this.closePopup(), 2200);
          }
        })
        .catch(() => {
          this.setLoading(false);
          this.showMessage('error');
        });
    }

    markSubmitted() {
      const days = parseInt(this.dataset.suppressAfterSubmit || '0', 10);
      if (days > 0) this.suppressFor(days);
    }

    suppressFor(days) {
      const key = this.storageKey();
      const until = Date.now() + days * 24 * 60 * 60 * 1000;
      try {
        localStorage.setItem(key, String(until));
      } catch (e) {
        /* storage unavailable, ignore */
      }
    }

    isSuppressed() {
      const frequency = this.dataset.frequency;
      if (frequency === 'always') return false;

      if (frequency === 'session') {
        try {
          return sessionStorage.getItem(this.storageKey()) === '1';
        } catch (e) {
          return false;
        }
      }

      try {
        const until = parseInt(localStorage.getItem(this.storageKey()) || '0', 10);
        return Date.now() < until;
      } catch (e) {
        return false;
      }
    }

    markSeenThisSession() {
      if (this.dataset.frequency !== 'session') return;
      try {
        sessionStorage.setItem(this.storageKey(), '1');
      } catch (e) {
        /* ignore */
      }
    }

    storageKey() {
      return 'form-builder-popup-' + this.id;
    }

    initPopup() {
      this.overlay = this.querySelector('.form-builder-overlay');
      this.trigger = this.querySelector('.form-builder-trigger');
      this.closeBtn = this.querySelector('.form-builder-close');
      const triggerMode = this.dataset.trigger;

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.closePopup(true));
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', (event) => {
          if (event.target === this.overlay) this.closePopup(true);
        });
      }
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.overlay && this.overlay.classList.contains('is-open')) {
          this.closePopup(true);
        }
      });

      if (triggerMode === 'button') {
        if (this.trigger) {
          this.trigger.hidden = false;
          this.trigger.addEventListener('click', () => this.openPopup());
        }
        return;
      }

      if (this.isSuppressed()) return;

      if (triggerMode === 'delay') {
        const seconds = parseFloat(this.dataset.delay || '5');
        window.setTimeout(() => {
          if (!this.isSuppressed()) this.openPopup();
        }, seconds * 1000);
      }

      if (triggerMode === 'exit_intent') {
        const onExit = (event) => {
          if (event.clientY <= 0 && !this.isSuppressed()) {
            this.openPopup();
            document.removeEventListener('mouseout', onExit);
          }
        };
        document.addEventListener('mouseout', onExit);
      }
    }

    openPopup() {
      if (!this.overlay) return;
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      this.markSeenThisSession();
      const firstField = this.overlay.querySelector('input, textarea, select');
      if (firstField) firstField.focus({ preventScroll: true });
    }

    closePopup(userClosed) {
      if (!this.overlay) return;
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      if (userClosed) {
        const days = parseInt(this.dataset.suppressAfterClose || '0', 10);
        if (days > 0) this.suppressFor(days);
      }
      if (this.trigger) this.trigger.focus({ preventScroll: true });
    }
  }

  customElements.define('form-builder-widget', FormBuilderWidget);
})();
